import React, { useState, useEffect, useCallback } from 'react'
import {
  ListItem,
  Collapse,
  List,
  Typography,
  TextField,
  InputAdornment,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core'
import { ExpandLess, ExpandMore } from '@material-ui/icons'
import Slider from '@material-ui/core/Slider'
import { useStyles as useAttributesFiltersStyles } from './AttributesFilters'
import CMSText from '../Global/CMSText'
import { PrismicShopData } from '../../interfaces/common/prismicShop'
import { AttributeType, PriceAttribute } from '../../typings/filters'

const usePriceFilterStyles = makeStyles((theme: Theme) =>
  createStyles({
    priceInputsContainer: {
      display: 'flex',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
      alignItems: 'center',
    },
    priceInput: {
      flex: 1,
      '& .MuiOutlinedInput-root': {
        borderRadius: 4,
      },
      '& .MuiOutlinedInput-input': {
        padding: theme.spacing(1.5),
        fontSize: 14,
      },
    },
    priceSeparator: {
      color: theme.extraColors?.greyText || '#666',
      fontSize: 14,
    },
    sliderWrapper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
  })
)

type Tprops = {
  attributeFilter: PriceAttribute
  handleExtend: (attributeFilter: PriceAttribute, type: string) => void
  t: any
  prismicShopData: PrismicShopData
  open: any
  priceRange: { price: number[]; takeBackPrice: number[] }
  rangeSelector: any
}

const PricesFilter = ({
  attributeFilter,
  handleExtend,
  t,
  prismicShopData,
  open,
  priceRange,
  rangeSelector,
}: Tprops) => {
  const attributeType = attributeFilter.type
  const attributeCode = attributeFilter.code
  const classes = useAttributesFiltersStyles()
  const priceClasses = usePriceFilterStyles()

  // Use Prismic values if defined, otherwise fall back to attributeFilter values
  const minLimit =
    prismicShopData?.filter_price_min ?? attributeFilter.minimum / 100
  const maxLimit =
    prismicShopData?.filter_price_max ?? attributeFilter.maximum / 100

  // Local state for text inputs to allow typing without immediate validation
  const [minInputValue, setMinInputValue] = useState<string>('')
  const [maxInputValue, setMaxInputValue] = useState<string>('')

  // Sync local state with priceRange when it changes (e.g., from slider)
  useEffect(() => {
    if (priceRange[attributeType]?.length === 2) {
      setMinInputValue(String(priceRange[attributeType][0]))
      setMaxInputValue(String(priceRange[attributeType][1]))
    }
  }, [priceRange, attributeType])

  // Handle min price input change
  const handleMinInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      // Allow empty or numeric values
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setMinInputValue(value)
      }
    },
    []
  )

  // Handle max price input change
  const handleMaxInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      // Allow empty or numeric values
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setMaxInputValue(value)
      }
    },
    []
  )

  // Validate and apply min price on blur
  const handleMinInputBlur = useCallback(() => {
    let newMin = parseFloat(minInputValue) || minLimit
    const currentMax = priceRange[attributeType]?.[1] ?? maxLimit

    // Ensure min is within bounds
    newMin = Math.max(minLimit, Math.min(newMin, currentMax))

    setMinInputValue(String(newMin))

    // Trigger the rangeSelector with the new values
    const syntheticEvent = {} as React.ChangeEvent<{}>
    rangeSelector(attributeType)(syntheticEvent, [newMin, currentMax])
  }, [
    minInputValue,
    minLimit,
    maxLimit,
    priceRange,
    attributeType,
    rangeSelector,
  ])

  // Validate and apply max price on blur
  const handleMaxInputBlur = useCallback(() => {
    let newMax = parseFloat(maxInputValue) || maxLimit
    const currentMin = priceRange[attributeType]?.[0] ?? minLimit

    // Ensure max is within bounds
    newMax = Math.max(currentMin, Math.min(newMax, maxLimit))

    setMaxInputValue(String(newMax))

    // Trigger the rangeSelector with the new values
    const syntheticEvent = {} as React.ChangeEvent<{}>
    rangeSelector(attributeType)(syntheticEvent, [currentMin, newMax])
  }, [
    maxInputValue,
    minLimit,
    maxLimit,
    priceRange,
    attributeType,
    rangeSelector,
  ])

  // Handle Enter key press to apply values
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent, isMin: boolean) => {
      if (event.key === 'Enter') {
        if (isMin) {
          handleMinInputBlur()
        } else {
          handleMaxInputBlur()
        }
      }
    },
    [handleMinInputBlur, handleMaxInputBlur]
  )

  return (
    <div key={attributeType}>
      <ListItem
        className={classes.filterListItem}
        onClick={() => handleExtend(attributeFilter, AttributeType.PRICE)}
      >
        <Typography
          variant={'body1'}
          component={'p'}
          className={classes.attributeTitle}
        >
          <CMSText
            defaultText={t('texts:shop:filter_price')}
            data={prismicShopData?.filter_price}
          />
        </Typography>
        {open.find((opened) => opened.value === attributeCode)?.open ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>
      <Collapse
        in={open.find((opened) => opened.value === attributeCode)?.open}
        timeout="auto"
        unmountOnExit
      >
        <List component="div" disablePadding>
          <div className={classes.sliderContainer}>
            <Typography id="range-slider" gutterBottom>
              <CMSText
                defaultText={t('texts:shop:price_fork')}
                data={prismicShopData?.price_fork}
              />
            </Typography>

            {/* Price input fields - only shown when display_price_range_inputs is true */}
            {prismicShopData?.display_price_range_inputs && (
              <div className={priceClasses.priceInputsContainer}>
                <TextField
                  className={priceClasses.priceInput}
                  variant="outlined"
                  size="small"
                  type="text"
                  inputMode="decimal"
                  placeholder={String(minLimit)}
                  value={minInputValue}
                  onChange={handleMinInputChange}
                  onBlur={handleMinInputBlur}
                  onKeyPress={(e) => handleKeyPress(e, true)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="textSecondary">
                          {prismicShopData?.price_range_inputs_placeholder_min ||
                            t('texts:shop:price_min_label')}
                        </Typography>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {attributeFilter.currency}
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography className={priceClasses.priceSeparator}>
                  —
                </Typography>
                <TextField
                  className={priceClasses.priceInput}
                  variant="outlined"
                  size="small"
                  type="text"
                  inputMode="decimal"
                  placeholder={String(maxLimit)}
                  value={maxInputValue}
                  onChange={handleMaxInputChange}
                  onBlur={handleMaxInputBlur}
                  onKeyPress={(e) => handleKeyPress(e, false)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="textSecondary">
                          {prismicShopData?.price_range_inputs_placeholder_max ||
                            t('texts:shop:price_max_label')}
                        </Typography>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {attributeFilter.currency}
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            )}

            {/* Slider */}
            <div className={priceClasses.sliderWrapper}>
              <Slider
                value={priceRange[attributeType]}
                onChange={rangeSelector(attributeType)}
                min={minLimit}
                valueLabelDisplay="auto"
                max={maxLimit}
              />
            </div>

            <Typography id="range-slider" gutterBottom>
              <CMSText
                defaultText={t('texts:shop:between')}
                data={prismicShopData?.between}
                asText
              />{' '}
              {`${priceRange[attributeType][0]} ${t('texts:shop:and')} ${
                priceRange[attributeType][1]
              } ${attributeFilter.currency}`}
            </Typography>
          </div>
        </List>
      </Collapse>
    </div>
  )
}

export default PricesFilter
